import React, { useState } from 'react';
import { X, Trash2, Plus, HelpCircle, LogOut, Palette } from 'lucide-react';
import IconPicker from '../common/IconPicker';
import Tooltip from '../common/Tooltip';
import CategoryIcon from '../common/CategoryIcon';
import ConfirmDialog from '../common/ConfirmDialog';
import SectionLabel from '../common/SectionLabel';
import Button from '../common/Button';
import PredictionHelpModal from './PredictionHelpModal';
import useAuth from '../../hooks/useAuth';
import useCategories from '../../hooks/useCategories';
import useToast from '../../hooks/useToast';
import { PREDICTION_OPTIONS, PREDICTION_DESCRIPTIONS } from '../../utils/predictionTypes';

const IconPickerPopup = ({ value, onSelect, onClose }) => (
  <div
    className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-[70]"
    onClick={(e) => { e.stopPropagation(); onClose(); }}
  >
    <div
      className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-4 space-y-3"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-neutral-800">Icon auswählen</h3>
        <button
          onClick={onClose}
          className="p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-all"
        >
          <X size={16} />
        </button>
      </div>
      <IconPicker value={value} onChange={(icon) => { onSelect(icon); onClose(); }} />
    </div>
  </div>
);

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

const ColorPickerPopup = ({ value, onSelect, onClose }) => {
  const [draft, setDraft] = useState(value || '#6366f1');
  const isValidHex = HEX_COLOR_RE.test(draft);

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-[70]"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-4 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-neutral-800">Farbe auswählen</h3>
          <button
            onClick={onClose}
            className="p-1.5 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-all"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="color"
            value={isValidHex ? draft : '#6366f1'}
            onChange={(e) => setDraft(e.target.value)}
            className="w-12 h-12 rounded-xl border border-neutral-200 cursor-pointer p-0.5 flex-shrink-0"
          />
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="#6366f1"
            className="flex-1 min-w-0 px-3 py-2.5 border border-neutral-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
          />
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={() => { onSelect(null); onClose(); }}
            className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-neutral-500 border border-dashed border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50 transition-all"
          >
            Zurücksetzen
          </button>
          <Button
            type="button"
            disabled={!isValidHex}
            onClick={() => { onSelect(draft); onClose(); }}
            className="flex-1 py-2.5 text-xs"
          >
            Übernehmen
          </Button>
        </div>
      </div>
    </div>
  );
};

const PredictionSegments = ({ value, onChange }) => (
  <div className="grid grid-cols-4 gap-1 bg-white border border-neutral-200 rounded-xl p-1">
    {PREDICTION_OPTIONS.map((opt) => (
      <button
        key={opt.value}
        type="button"
        title={opt.label}
        onClick={() => onChange(opt.value)}
        className={`px-1 py-1.5 rounded-lg text-[11px] font-semibold truncate transition-all ${
          (value || '') === opt.value
            ? 'bg-primary-600 text-white shadow-sm'
            : 'text-neutral-500 hover:bg-neutral-100'
        }`}
      >
        {opt.short}
      </button>
    ))}
  </div>
);

const AnalyticsCheckbox = ({ checked, onChange }) => (
  <label className="flex items-center gap-2 text-xs text-neutral-500 cursor-pointer select-none">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
    />
    Auswerten &amp; Vorhersagen
  </label>
);

const EMPTY_NEW_CATEGORY = {
  name: '',
  icon_name: 'HelpCircle',
  color: null,
  prediction_type: '',
  include_in_analytics: true
};

const SettingsModal = ({ onClose }) => {
  const { apiFetch, logout } = useAuth();
  const { categories, refetchCategories } = useCategories();
  const { showToast } = useToast();

  const [showAddForm, setShowAddForm] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [newCategory, setNewCategory] = useState(EMPTY_NEW_CATEGORY);
  const [nameEdit, setNameEdit] = useState(null);
  const [iconPickerFor, setIconPickerFor] = useState(null);
  const [colorPickerFor, setColorPickerFor] = useState(null);
  const [error, setError] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const closeAddForm = () => {
    setShowAddForm(false);
    setNewCategory(EMPTY_NEW_CATEGORY);
  };

  const reportError = (message) => {
    setError(message);
    showToast(message);
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name.trim()) return;
    setError('');
    try {
      const response = await apiFetch('/api/categories', {
        method: 'POST',
        body: JSON.stringify({
          name: newCategory.name.trim(),
          icon_name: newCategory.icon_name,
          color: newCategory.color,
          prediction_type: newCategory.prediction_type || null,
          include_in_analytics: newCategory.include_in_analytics
        })
      });
      if (response.ok) {
        closeAddForm();
        refetchCategories();
      } else {
        const data = await response.json();
        reportError(data.message || 'Fehler beim Hinzufügen der Kategorie');
      }
    } catch (err) {
      reportError('Verbindungsfehler');
    }
  };

  const saveCategory = async (cat, patch) => {
    setError('');
    try {
      const response = await apiFetch(`/api/categories/${cat.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: cat.name,
          icon_name: cat.icon_name,
          color: cat.color || null,
          prediction_type: cat.prediction_type || null,
          include_in_analytics: Boolean(cat.include_in_analytics),
          ...patch
        })
      });
      if (response.ok) {
        refetchCategories();
      } else {
        const data = await response.json();
        reportError(data.message || 'Fehler beim Aktualisieren der Kategorie');
      }
    } catch (err) {
      reportError('Verbindungsfehler');
    }
  };

  const commitName = (cat) => {
    if (!nameEdit || nameEdit.id !== cat.id) return;
    const trimmed = nameEdit.value.trim();
    setNameEdit(null);
    if (trimmed && trimmed !== cat.name) {
      saveCategory(cat, { name: trimmed });
    }
  };

  const confirmDeleteCategory = async () => {
    const id = deleteConfirmId;
    setDeleteConfirmId(null);
    setError('');
    try {
      const response = await apiFetch(`/api/categories/${id}`, { method: 'DELETE' });
      if (response.ok) {
        refetchCategories();
      } else {
        const data = await response.json();
        reportError(data.message || 'Fehler beim Löschen der Kategorie');
      }
    } catch (err) {
      reportError('Verbindungsfehler');
    }
  };

  const iconPickerCategory = iconPickerFor !== null && iconPickerFor !== 'new'
    ? categories.find(c => c.id === iconPickerFor)
    : null;

  const colorPickerCategory = colorPickerFor !== null && colorPickerFor !== 'new'
    ? categories.find(c => c.id === colorPickerFor)
    : null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end md:items-center justify-center md:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white shadow-2xl w-full md:max-w-2xl overflow-hidden border border-neutral-100 max-h-[92vh] md:max-h-[90vh] flex flex-col rounded-t-3xl md:rounded-3xl animate-sheet-up md:animate-none pb-[env(safe-area-inset-bottom)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (mobile only) */}
        <div className="flex justify-center pt-3 pb-1 md:hidden flex-none">
          <div className="w-10 h-1 bg-neutral-200 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 flex-none">
          <div>
            <h2 className="text-lg font-bold text-neutral-800">Kategorien verwalten</h2>
            <p className="text-xs text-neutral-400 mt-0.5">{categories.length} Kategorien</p>
          </div>
          <div className="flex items-center gap-1">
            <Tooltip content="Vorhersagen erklärt" position="left">
              <button
                onClick={() => setShowHelp(true)}
                className="p-2 text-neutral-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all"
              >
                <HelpCircle size={19} />
              </button>
            </Tooltip>
            <button
              onClick={onClose}
              className="p-2 text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 rounded-xl transition-all"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {/* Add Category */}
          {!showAddForm ? (
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-neutral-200 text-neutral-500 py-3 px-4 rounded-2xl font-semibold text-sm hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50/50 transition-all"
            >
              <Plus size={16} />
              Neue Kategorie
            </button>
          ) : (
            <form onSubmit={handleAddCategory} className="bg-neutral-50 rounded-xl border border-primary-200 p-3 space-y-2.5">
              <div className="flex items-center gap-2.5">
                <Tooltip content="Icon wählen" position="right">
                  <button
                    type="button"
                    onClick={() => setIconPickerFor('new')}
                    className="w-9 h-9 bg-white rounded-lg border border-neutral-200 flex items-center justify-center flex-shrink-0 hover:border-primary-300 hover:bg-primary-50 transition-all group"
                  >
                    <CategoryIcon name={newCategory.icon_name} size={17} className="text-neutral-500 group-hover:text-primary-600 transition-colors" />
                  </button>
                </Tooltip>
                <Tooltip content="Farbe wählen" position="right">
                  <button
                    type="button"
                    onClick={() => setColorPickerFor('new')}
                    className="w-9 h-9 rounded-lg border border-neutral-200 flex items-center justify-center flex-shrink-0 hover:border-primary-300 transition-all"
                    style={newCategory.color ? { backgroundColor: newCategory.color } : {}}
                  >
                    {!newCategory.color && <Palette size={15} className="text-neutral-400" />}
                  </button>
                </Tooltip>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="Kategoriename..."
                  autoFocus
                  className="flex-1 min-w-0 px-3 py-1.5 border border-neutral-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                />
              </div>

              <AnalyticsCheckbox
                  checked={newCategory.include_in_analytics}
                  onChange={(checked) => setNewCategory({ ...newCategory, include_in_analytics: checked })}
              />

              {newCategory.include_in_analytics && (
                <PredictionSegments
                  value={newCategory.prediction_type}
                  onChange={(v) => setNewCategory({ ...newCategory, prediction_type: v })}
                />
              )}

              {newCategory.prediction_type && PREDICTION_DESCRIPTIONS[newCategory.prediction_type] && (
                <p className="text-xs text-neutral-500 bg-blue-50 px-3 py-2 rounded-xl border border-blue-100">
                  {PREDICTION_DESCRIPTIONS[newCategory.prediction_type]}
                </p>
              )}

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeAddForm}
                  className="flex-1 py-2 px-4 rounded-xl font-semibold text-sm text-neutral-500 border border-neutral-200 hover:bg-neutral-100 transition-all"
                >
                  Abbrechen
                </button>
                <Button
                  type="submit"
                  disabled={!newCategory.name.trim()}
                  className="flex-1 flex items-center justify-center gap-2 py-2 px-4 text-sm"
                >
                  <Plus size={15} />
                  Hinzufügen
                </Button>
              </div>
            </form>
          )}

          {error && (
            <div className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* Categories List */}
          <div>
            <SectionLabel as="h3" className="block mb-3">Vorhandene Kategorien</SectionLabel>
            {categories.length === 0 ? (
              <p className="text-center text-neutral-400 text-sm py-8">Noch keine Kategorien vorhanden.</p>
            ) : (
              <div className="space-y-2">
                {categories.map((cat) => (
                  <div key={cat.id} className="bg-neutral-50 rounded-xl border border-neutral-100 hover:border-neutral-200 transition-all p-3 space-y-2.5">
                    <div className="flex items-center gap-2.5">
                      <Tooltip content="Icon ändern" position="right">
                        <button
                          type="button"
                          onClick={() => setIconPickerFor(cat.id)}
                          className="w-9 h-9 bg-white rounded-lg border border-neutral-200 flex items-center justify-center flex-shrink-0 hover:border-primary-300 hover:bg-primary-50 transition-all group"
                        >
                          <CategoryIcon name={cat.icon_name} size={17} className="text-neutral-500 group-hover:text-primary-600 transition-colors" />
                        </button>
                      </Tooltip>
                      <Tooltip content="Farbe ändern" position="right">
                        <button
                          type="button"
                          onClick={() => setColorPickerFor(cat.id)}
                          className="w-9 h-9 rounded-lg border border-neutral-200 flex items-center justify-center flex-shrink-0 hover:border-primary-300 transition-all"
                          style={cat.color ? { backgroundColor: cat.color } : {}}
                        >
                          {!cat.color && <Palette size={15} className="text-neutral-400" />}
                        </button>
                      </Tooltip>

                      {nameEdit?.id === cat.id ? (
                        <input
                          type="text"
                          value={nameEdit.value}
                          autoFocus
                          onChange={(e) => setNameEdit({ id: cat.id, value: e.target.value })}
                          onBlur={() => commitName(cat)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitName(cat);
                            if (e.key === 'Escape') setNameEdit(null);
                          }}
                          className="flex-1 min-w-0 px-2 py-1 border border-neutral-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => setNameEdit({ id: cat.id, value: cat.name })}
                          title="Zum Umbenennen klicken"
                          className="flex-1 min-w-0 text-left text-sm font-semibold text-neutral-700 truncate px-2 py-1 rounded-lg hover:bg-white hover:ring-1 hover:ring-neutral-200 transition-all"
                        >
                          {cat.name}
                        </button>
                      )}

                      <Tooltip content="Löschen" position="left">
                        <button
                          onClick={() => setDeleteConfirmId(cat.id)}
                          className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all flex-shrink-0"
                        >
                          <Trash2 size={15} />
                        </button>
                      </Tooltip>
                    </div>

                    <AnalyticsCheckbox
                        checked={Boolean(cat.include_in_analytics)}
                        onChange={(checked) => saveCategory(cat, { include_in_analytics: checked })}
                    />

                    {Boolean(cat.include_in_analytics) && (
                      <PredictionSegments
                        value={cat.prediction_type || ''}
                        onChange={(v) => saveCategory(cat, { prediction_type: v || null })}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-neutral-100">
            <button
              type="button"
              onClick={logout}
              className="w-full flex items-center justify-center gap-2 text-neutral-400 hover:text-red-600 hover:bg-red-50 py-2.5 px-4 rounded-xl font-semibold text-sm transition-all"
            >
              <LogOut size={16} />
              Abmelden
            </button>
          </div>
        </div>
      </div>

      {iconPickerFor === 'new' && (
        <IconPickerPopup
          value={newCategory.icon_name}
          onSelect={(icon) => setNewCategory({ ...newCategory, icon_name: icon })}
          onClose={() => setIconPickerFor(null)}
        />
      )}
      {iconPickerCategory && (
        <IconPickerPopup
          value={iconPickerCategory.icon_name}
          onSelect={(icon) => saveCategory(iconPickerCategory, { icon_name: icon })}
          onClose={() => setIconPickerFor(null)}
        />
      )}

      {colorPickerFor === 'new' && (
        <ColorPickerPopup
          value={newCategory.color}
          onSelect={(color) => setNewCategory({ ...newCategory, color })}
          onClose={() => setColorPickerFor(null)}
        />
      )}
      {colorPickerCategory && (
        <ColorPickerPopup
          value={colorPickerCategory.color}
          onSelect={(color) => saveCategory(colorPickerCategory, { color })}
          onClose={() => setColorPickerFor(null)}
        />
      )}

      {showHelp && <PredictionHelpModal onClose={() => setShowHelp(false)} />}

      {deleteConfirmId !== null && (
        <ConfirmDialog
          title="Kategorie löschen?"
          message="Das kann sich auf Einträge mit dieser Kategorie auswirken."
          onConfirm={confirmDeleteCategory}
          onCancel={() => setDeleteConfirmId(null)}
        />
      )}
    </div>
  );
};

export default SettingsModal;
